import { Auth } from 'aws-amplify'
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { gql } from '@apollo/client'

import { IconTypes } from '@app/ui'
import env from '@app/environment'
import { publicClient } from '@app/apollo-client'
import {
  AFFILIATE_LINK_INTERNAL_NAME_KEY,
  FOLLOW_TIPSTER_LINK_INTERNAL_NAME_KEY,
  VIP_INVITE_TOKEN_KEY,
  INSTALL_QUERY,
  INSTALL_TIPSTER_LINK_INTERNAL_NAME_KEY,
  INVITE_LINK_INTERNAL_NAME_KEY,
  INVITEE_TIPSTER_LINK_INTERNAL_NAME_KEY,
  PosthogEvent,
  PosthogParamEventName,
  PosthogParamId,
  ROUTES,
  SEARCH_PARAM_VALUES,
  SEARCH_PARAMS
} from '@app/constants'
import { CurrentUserState, exitGuestMode, UserAuthState } from '@app/storage'
import posthog from 'posthog-js'
import { UserOnboardingStages, UserRoles } from '@app/graphql'
import { isSignInWithEmailLink, signInWithCustomToken, signInWithEmailLink } from 'firebase/auth'
import { auth, channel, ensurePersistence, firebaseUser, getFirebaseToken } from '@app/firebase'
import { captureAnalytics, posthogGetDistinctId } from './misc'
import { syncPwaProperties } from './pwaTracking'

export const authByFacebook = async (queryParams = '') => {
  try {
    localStorage.setItem('vba_authQueryParams', queryParams)
    const credentials = await Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Facebook,
      customState: encodeURIComponent(queryParams),
    })
    CurrentUserState.auth_type = 'facebook'
    exitGuestMode()
    return { data: credentials }
  } catch (e) {
    return { error: e }
  }
}

export const restoreAuthQueryParams = () => {
  let getQuery = getAuthParams() || JSON.parse(localStorage.getItem('vba_authQueryParams') || null)
  getQuery = Object.fromEntries(Object.entries(getQuery).filter(([ key, value ]) => value))
  const searchQuery = new URLSearchParams(getQuery)
  window.history.replaceState(null, '', `${window.location.origin}${window.location.pathname}?${searchQuery.toString()}`)
  localStorage.removeItem('vba_authQueryParams')
}


const POPUP_FEATURES = (() => {
  const w = 520, h = 640
  const left = Math.round(window.screenX + (window.outerWidth - w) / 2)
  const top = Math.round(window.screenY + (window.outerHeight - h) / 2)
  return `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,location=no`
})()

// Module-level slot for the pre-opened blank popup window.
// Must be opened synchronously inside a click handler (before any await)
// so the browser accepts window.open as user-initiated.
let _pendingGooglePopup: Window | null = null

// Call this SYNCHRONOUSLY at the very start of the Google login click handler,
// before any await, so the browser treats it as user-initiated.
// Only opens a popup when the app is embedded in an iframe.
export const prepareGoogleAuthPopup = (): void => {
  if (window.top === window) return
  _pendingGooglePopup = window.open('about:blank', 'vb-google-auth', POPUP_FEATURES)
}

// Navigates the pre-opened popup to /?googleAuthPopup=1.
// The popup runs Auth.federatedSignIn() as a top-level window — Google allows this.
// On return from Cognito, PopupAuthComplete signals the iframe via BroadcastChannel.
const authByGooglePopup = (queryParams = ''): void => {
  localStorage.setItem('vba_authQueryParams', queryParams)
  localStorage.setItem('auth_type', 'google')

  const popup = _pendingGooglePopup && !_pendingGooglePopup.closed
    ? _pendingGooglePopup
    : window.open('about:blank', 'vb-google-auth', POPUP_FEATURES)

  _pendingGooglePopup = null

  if (popup) {
    const params = new URLSearchParams({ googleAuthPopup: '1' })
    if (queryParams) params.set('vbaState', encodeURIComponent(queryParams))
    const target = `${window.location.origin}/?${params.toString()}`
    popup.location.href = target
  } else {
    console.warn('[vb:auth] popup was blocked or null')
  }
}

export const authByGoogle = async (queryParams = '') => {
  // When embedded in an iframe, use popup-based auth instead of a full-page redirect.
  // A redirect inside the iframe hits Google's iframe block and returns 403.
  if (window.top !== window) {
    authByGooglePopup(queryParams)
    return { data: null }
  }

  try {
    const customState = encodeURIComponent(queryParams)
    localStorage.setItem('vba_authQueryParams', queryParams)

    const credentials = await Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google,
      customState,
    })
    CurrentUserState.auth_type = 'google'
    exitGuestMode()
    return { data: credentials }
  } catch (e) {
    return { error: e }
  }
}

export const handleSendMagicLink = async (emailText: string, trackSignupStart, isResend=false) => {
  try {
    await sendMagicLinkEmail(emailText)

    if (isResend) return
    const sessionId = posthogGetDistinctId()
    captureAnalytics(PosthogEvent.AUTH_VIA_MAGIC_LINK, {
      magic_link_email: emailText
    })
    trackSignupStart({
      variables: {
        email: emailText,
        sessionId,
        role: UserRoles.User
      },
    })

  } catch (error) {
    console.error('Error sending magic link:', error)
    throw new Error('Failed to send magic link. Please try again.')
  }
}

const buildAuthRedirectUrl = (): string => {
  const authParams = new URLSearchParams(getAuthParams())
  authParams.append(PosthogParamId, posthogGetDistinctId())
  authParams.append(PosthogParamEventName, 'magic_link_click')

  // When embedded in an iframe (e.g. a casino page), point the Firebase
  // continueUrl at the PARENT page instead of this app. The casino page then
  // detects iframeMagicLink=1, sets the iframe src to
  // `pwaOrigin/auth-callback?iframeMagicLink=<encoded_full_link>`, and the
  // iframe completes the sign-in. This keeps the user inside the casino.
  //
  // Use document.referrer first; fall back to the URL stored on initial iframe
  // load (Application.tsx useEffect) in case Referrer-Policy blocks it.
  const isIframe = window.top !== window
  // Prefer the full URL received via postMessage (stored by Application.tsx on mount)
  // over document.referrer, which Referrer-Policy strips to origin-only (no path).
  // Without this, the continueUrl lands on "/" instead of "/tips" and the casino's
  // iframeMagicLink handler never runs.
  const parentReferrer = localStorage.getItem('vb_iframe_parent_url') || document.referrer || ''

  console.log('[vb:ml] buildAuthRedirectUrl — isIframe:', isIframe, 'parentReferrer:', parentReferrer)

  if (isIframe && parentReferrer) {
    try {
      const parentUrl = new URL(parentReferrer)
      authParams.forEach((value, key) => parentUrl.searchParams.set(key, value))
      parentUrl.searchParams.set('iframeMagicLink', '1')
      parentUrl.searchParams.set('pwaOrigin', window.location.origin)
      const finalUrl = parentUrl.toString()
      console.log('[vb:ml] continueUrl (iframe):', finalUrl)
      return finalUrl
    } catch {
      // fallthrough to default
    }
  }

  const baseUrl = `${window.location.protocol}//${window.location.host}`
  const finalUrl = `${baseUrl}${ROUTES.authCallback.short}?${authParams.toString()}`
  console.log('[vb:ml] continueUrl (direct):', finalUrl)
  return finalUrl
}

const storeAuthState = (email: string): void => {
  localStorage.setItem('emailForSignIn', email)
  localStorage.setItem('auth_type', 'magic_link')
  localStorage.setItem('magic_link_email', email)
}

const SEND_MAGIC_LINK_MUTATION = gql`
  mutation SendMagicLink($input: SendMagicLinkInput!) {
    sendMagicLink(input: $input)
  }
`

export const sendMagicLinkEmail = async (email: string) => {
  if (!email?.trim()) {
    throw new Error('Email is required')
  }

  const redirectUrl = buildAuthRedirectUrl()

  const result = await publicClient.mutate<{ sendMagicLink: boolean }>({
    mutation: SEND_MAGIC_LINK_MUTATION,
    variables: { input: { email, redirectUrl } },
  })

  if (!result.data?.sendMagicLink) {
    throw new Error('Failed to send magic link')
  }

  storeAuthState(email)
}

let magicLinkSignIn: ReturnType<typeof runMagicLinkSignIn> | null = null

export const authByMagicLink = () => {
  if (!magicLinkSignIn) {
    magicLinkSignIn = runMagicLinkSignIn().finally(() => {
      magicLinkSignIn = null
    })
  }
  return magicLinkSignIn
}

const runMagicLinkSignIn = async () => {
  const callbackParams = new URLSearchParams(window.location.search)
  // When coming from an iframe via the casino page handler, the full Firebase
  // magic link URL is passed as the iframeMagicLink param. Otherwise use the
  // current page URL directly.
  const url = callbackParams.get('iframeMagicLink') ?? window.location.href

  console.log('[vb:ml] authByMagicLink called')
  console.log('[vb:ml] window.location.href:', window.location.href)
  console.log('[vb:ml] iframeMagicLink param:', callbackParams.get('iframeMagicLink'))
  console.log('[vb:ml] resolved url for sign-in:', url)

  await ensurePersistence()
  const user = await firebaseUser()
  console.log('[vb:ml] firebaseUser result:', user ? `uid=${user.uid}` : 'null')

  if(user) {
    const expectedEmail = localStorage.getItem('magic_link_email')
      ?? new URLSearchParams(window.location.search).get('email')
    const emailMatches = !expectedEmail || user.email?.toLowerCase() === expectedEmail.trim().toLowerCase()
    if (emailMatches) {
      // Set isAuth before the async getIdToken call so that onAuthChange →
      // initiateOnboarding doesn't race ahead of it (TR-814 first-visit flicker).
      localStorage.setItem('auth_type', 'magic_link')
      localStorage.setItem('isAuth', 'true')
      CurrentUserState.auth_type = 'magic_link'
      exitGuestMode()
      let idToken: string
      try {
        idToken = await user.getIdToken(true)
      } catch (tokenErr) {
        // Firebase returns 400 when the refresh token is invalid/revoked (e.g. session
        // created by a magic link that has been consumed or expired). Sign out the
        // broken session so downstream code doesn't retry indefinitely.
        console.warn('[vb:ml] getIdToken failed — signing out stale session:', tokenErr)
        await auth.signOut()
        UserAuthState.invalidMagicLink = true
        return
      }
      restoreAuthQueryParams()
      const payload = { user, idToken }
      localStorage.removeItem('magic_link_email')
      return payload
    }
    // A different account is signed in — sign it out so signInWithEmailLink
    // can complete for the intended email address.
    console.log('[vb:ml] stale session for', user.email, '— expected', expectedEmail, ', signing out')
    await auth.signOut()
  }

  const isValidLink = isSignInWithEmailLink(auth, url)
  console.log('[vb:ml] isSignInWithEmailLink:', isValidLink, 'for url:', url)

  if(!isValidLink) {
    UserAuthState.invalidMagicLink = true
    return
  }

  // magic_link_email is set in the iframe's partitioned localStorage when the
  // user requests the link. When the casino handler loads the iframe at
  // /auth-callback?iframeMagicLink=..., the email is also in the URL params as
  // a fallback (set by buildAuthRedirectUrl).
  const email = localStorage.getItem('magic_link_email')
    ?? new URL(url).searchParams.get('email')

  if (!email) {
    UserAuthState.noMagicLinkEmail = true
    return
  }

  console.log('[vb:ml] calling signInWithEmailLink...')
  let userCredential
  try {
    userCredential = await signInWithEmailLink(auth, email, url)
    console.log('[vb:ml] signInWithEmailLink SUCCESS, uid:', userCredential.user.uid)
  } catch (err) {
    console.error('[vb:ml] signInWithEmailLink FAILED:', err)
    throw err
  }

  // Set isAuth IMMEDIATELY after sign-in and before the async getIdToken call.
  // Firebase's onAuthChange fires in a queued microtask after signInWithEmailLink
  // resolves — if isAuth is not set here, the onAuthChange → fetchCurrentUser →
  // USER_NOT_REGISTERED → initiateOnboarding=true chain races ahead of isAuth,
  // causing OnboardingRedirectGuard to see isAuth=null and navigate to ROUTES.root
  // (the entry screen), producing the TR-814 first-visit flicker.
  localStorage.setItem('auth_type', 'magic_link')
  localStorage.setItem('isAuth', 'true')
  CurrentUserState.auth_type = 'magic_link'
  exitGuestMode()

  channel.postMessage({ type: 'MAGIC_LINK_USED' })
  const idToken = await userCredential.user.getIdToken(true)
  console.log('[vb:ml] got idToken')
  restoreAuthQueryParams()
  localStorage.removeItem('magic_link_email')
  const payload = { user: userCredential.user, idToken }
  return payload
}

export const getCurrentUser = async () => {
  const maxRetries = sessionStorage.getItem('incognitoMode')? 1: 2
  const baseDelay = 500
  const authType = localStorage.getItem('auth_type')
  for(let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      let data = null
      if (authType === 'telegram') {
        const raw = localStorage.getItem('telegram_user')
        return { data: JSON.parse(raw) }
      } else if (authType === 'magic_link') {
        data = await authByMagicLink()
      } else {
        data = await Auth.currentAuthenticatedUser()
      }
      if (data) exitGuestMode()
      localStorage.setItem('isAuth', 'true')
      return { data }
    } catch (e) {
      if(attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt - 1)
        await new Promise(resolve => setTimeout(resolve, delay))
      } else {
        if(authType === 'magic_link') {
          UserAuthState.invalidMagicLink = true
        }
        if(authType !== 'sso') {
          localStorage.removeItem('isAuth')
        }
        return { error: e }
      }
    }
  }
}

export const getUserAttributes = async (): Promise<{ name: string, family_name: string, email: string }> => {
  try {
    const data = await Auth.currentUserInfo()
    return data?.attributes || null
  } catch (e) {
    console.error('get user attributes Error: ', e)
    return null
  }
}
const STORAGE_KEYS_TO_CLEAR = [
  'isAuth',
  'auth_type',
  'newChannelId',
  'useProgreeBarLoader',
  'currentTab',
  'onboardingFlow',
  'magic_link_token',
  'posthog_aliased_users'
] as const

const AUTH_SPECIFIC_STORAGE: Record<string, string[]> = {
  telegram: [ 'telegram_user' ],
  magic_link: [ 'magic_link' ]
}

const clearStorageItems = (authType: string | null): void => {
  STORAGE_KEYS_TO_CLEAR.forEach(key => localStorage.removeItem(key))

  if (authType && AUTH_SPECIFIC_STORAGE[authType]) {
    AUTH_SPECIFIC_STORAGE[authType].forEach(key => localStorage.removeItem(key))
  }

  // When inside an iframe, Cognito tokens were explicitly copied into the iframe's
  // partitioned storage during login — clear them here since Auth.signOut() may not
  // reach them in all browser storage-partitioning configurations.
  if (window.top !== window) {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (
        key.startsWith('CognitoIdentityServiceProvider') ||
        key === 'amplify-signin-with-hostedUI'
      )) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }

  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('posthog_session_')) {
      sessionStorage.removeItem(key)
    }
  })
}

const handleAuthProviderLogout = async (authType: string | null): Promise<void> => {
  if (authType !== 'google' && authType !== 'facebook') return
  try {
    await Auth.signOut()
    // Skip the Cognito logout redirect when running inside an iframe.
    // Navigating window.location inside the iframe would either break out of the
    // casino host or show a DNS error. Auth.signOut() already clears the local
    // Amplify session — the redirect is only needed for full SSO invalidation.
    if (window.top !== window) return
    const oauthHost = env.AWS_OAUTH_DOMAIN.includes('.')
      ? env.AWS_OAUTH_DOMAIN
      : `${env.AWS_OAUTH_DOMAIN}.auth.${env.AWS_PROJECT_REGION}.amazoncognito.com`
    const logoutUrl = `https://${oauthHost}/logout` +
    `?client_id=${env.AWS_USER_POOLS_WEB_CLIENT_ID}&logout_uri=${env.AWS_OAUTH_REDIRECT_SIGN_OUT}`
    window.location.href = logoutUrl
  } catch (error) {
    console.warn('Cognito signOut failed:', error)
  }
}

const firebaseSignOut = async (): Promise<void> => {
  try {
    await auth.signOut()
  } catch (error) {
    console.warn('Firebase signOut failed:', error)
  }
}

// Errors that mean the sign-in session is really gone (signed out, refresh token
// revoked or expired, account disabled or deleted). Anything else, such as a
// network failure or throttling, leaves the session in place.
const COGNITO_SESSION_GONE_CODES = [ 'NotAuthorizedException', 'UserNotFoundException' ]
const FIREBASE_SESSION_GONE_CODES = [
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-user-token',
]

const hasErrorCode = (error: unknown, codes: string[]): boolean =>
  codes.includes((error as { code?: string } | null)?.code ?? '')

const isCognitoSessionGone = async (): Promise<boolean> => {
  try {
    await Auth.currentSession()
    return false
  } catch (error) {
    return error === 'No current user' || hasErrorCode(error, COGNITO_SESSION_GONE_CODES)
  }
}

const isFirebaseSessionGone = async (): Promise<boolean> => {
  if (!auth) return true
  try {
    await ensurePersistence()
    const user = auth.currentUser ?? await firebaseUser()
    if (!user) return true
    await user.getIdToken()
    return false
  } catch (error) {
    return hasErrorCode(error, FIREBASE_SESSION_GONE_CODES)
  }
}

// Decides whether a failed current-user query should log the user out. Only a
// gone session counts: a backend outage must not sign anyone out.
export const isAuthSessionGone = async (): Promise<boolean> => {
  switch (localStorage.getItem('auth_type')) {
    case 'google':
    case 'facebook':
      return isCognitoSessionGone()
    case 'magic_link':
    case 'sso':
      return isFirebaseSessionGone()
    case 'telegram':
      return !localStorage.getItem('telegram_user')
    default:
      return true
  }
}

export const logOut = async () => {
  try {
    const authType = localStorage.getItem('auth_type')

    clearStorageItems(authType)
    exitGuestMode()
    resetPostHog()

    const { UserCacheHelper } = await import('./userCache')
    UserCacheHelper.clear()

    if(auth) firebaseSignOut()
    await handleAuthProviderLogout(authType)

    return { success: true }
  } catch (e) {
    return { error: e, success: false }
  }
}

const resetPostHog = () => {
  try {
    posthog.reset()
    posthog.group('tenant', window.location.hostname, { domain: window.location.hostname })
    syncPwaProperties()
  } catch (posthogError) {
    console.error('PostHog reset error:', posthogError)
  }
}

export const validateEmail = (email: string) => {
  return email != null && email.toLowerCase().match(
    new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
  )
}

export const validatePhoneNumber = (phone: string): boolean => {
  return new RegExp(/^\+?(\d*\s?\d+)*\s?$/).test(phone)
}

export const validateDisplayName = (name: string): boolean => {
  return !new RegExp(/[-!$%^&*()_+|~=`\\#{}\[\]:";'<>?,.\/@±§]/g).test(name) // eslint-disable-line no-useless-escape
}

export const shareLinks = (link: string) => {
  const encodedLink = encodeURIComponent(link || '')

  const telegram = {
    name: 'Telegram',
    link: `https://telegram.me/share/url?url=${encodedLink}`,
    iconType: IconTypes.telegram,
  }
  const facebook = {
    name: 'Facebook',
    link: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`,
    iconType: IconTypes.facebook,
  }
  const whatsapp = {
    name: 'WhatsApp',
    link: `https://api.whatsapp.com/send?text=${encodedLink}`,
    iconType: IconTypes.whatsApp,
  }
  const twitter = {
    name: 'Twitter',
    link: `https://twitter.com/intent/tweet?url=${encodedLink}`,
    iconType: IconTypes.twitter,
  }
  const viber = {
    name: 'Viber',
    link: `viber://forward?text=${encodedLink}`,
    iconType: IconTypes.viber,
  }

  return { telegram, facebook, whatsapp, twitter, viber }
}

export const getAuthParams = () => {
  const urlPrams = new URLSearchParams(window.location.search)
  const params = {
    ...Object.fromEntries(urlPrams.entries()),
    vbph_sid: posthog.get_session_id(),
    vbph_did: posthog.get_distinct_id(),
  }
  return params
}

export const getAuthSerializedString = ({ affiliateLinkName, inviteLinkName, followLinkName, install }) => {
  const authParam = getAuthParams()
  if (affiliateLinkName) {
    return JSON.stringify({ [AFFILIATE_LINK_INTERNAL_NAME_KEY]: affiliateLinkName, ...authParam })
  } else if (inviteLinkName) {
    return JSON.stringify({ [INVITE_LINK_INTERNAL_NAME_KEY]: inviteLinkName, ...authParam })
  } else if (followLinkName) {
    return JSON.stringify({ [FOLLOW_TIPSTER_LINK_INTERNAL_NAME_KEY]: followLinkName, ...authParam })
  } else if (install) {
    return JSON.stringify({ [INSTALL_TIPSTER_LINK_INTERNAL_NAME_KEY]: install, ...authParam })
  }

  return Object.keys(authParam).length? JSON.stringify(authParam): null
}

export const getSearchParams = () => {
  const params = new URLSearchParams(window.location.search)
  return {
    affiliateLinkName: params?.get(AFFILIATE_LINK_INTERNAL_NAME_KEY),
    inviteLinkName: params?.get(INVITE_LINK_INTERNAL_NAME_KEY),
    followLinkName: params?.get(FOLLOW_TIPSTER_LINK_INTERNAL_NAME_KEY),
    inviteeTipsterLinkName: params?.get(INVITEE_TIPSTER_LINK_INTERNAL_NAME_KEY),
    installLinkName: params?.get(INSTALL_TIPSTER_LINK_INTERNAL_NAME_KEY),
    installQuery: params?.get(INSTALL_QUERY),
    vipInviteToken: params?.get(VIP_INVITE_TOKEN_KEY),
  }
}
// todo: temporary forbid to create new users
export const checkExistingProdUser = (): boolean => {
  const params = new URLSearchParams(window.location.search)
  const existingUserParam = params?.get(SEARCH_PARAMS.existingUser)
  return existingUserParam !== SEARCH_PARAM_VALUES[SEARCH_PARAMS.existingUser] && env.APP_ENVIRONMENT === 'prod'
}

export const prepareInviteLink = (linkName: string): string => {
  if (!linkName) return null
  const domain = window.location.origin
  return encodeURI(domain + '?' + INVITE_LINK_INTERNAL_NAME_KEY + '=' + linkName)
}
export const prepareInstallLink = (linkName: string): string => {
  if (!linkName) return null
  const domain = window.location.origin
  return encodeURI(domain + '/install?' + INSTALL_TIPSTER_LINK_INTERNAL_NAME_KEY + '=' + linkName)
}
export const prepareFollowLink = (linkName: string): string => {
  if (!linkName) return null
  const domain = window.location.origin
  return encodeURI(domain + '?' + FOLLOW_TIPSTER_LINK_INTERNAL_NAME_KEY + '=' + linkName)
}

export const copyInviteLink = (linkName: string): boolean => {
  if (linkName && 'clipboard' in navigator) {
    const encodedUrl = prepareInviteLink(linkName)
    navigator.clipboard.writeText(encodedUrl)
    return true
  }
  return false
}
export const copyInstallLink = (linkName: string): boolean => {
  if (linkName && 'clipboard' in navigator) {
    const encodedUrl = prepareInstallLink(linkName)
    navigator.clipboard.writeText(encodedUrl)
    return true
  }
  return false
}

// ---------------------------------------------------------------------------
// SSO login — called from the SSO_LOGIN postMessage listener in Application.tsx
// ---------------------------------------------------------------------------

const SSO_LOGIN_MUTATION = gql`
  mutation SsoLogin($input: SsoLoginInput!) {
    ssoLogin(input: $input) {
      firebaseToken
    }
  }
`

const SSO_RETRY_DELAYS_MS = [ 500, 1500 ]

export class SsoLoginError extends Error {
  attempts: number

  constructor(message: string, attempts: number) {
    super(message)
    this.name = 'SsoLoginError'
    this.attempts = attempts
  }
}

const attemptSsoExchange = async (provider: string, payload: unknown): Promise<void> => {
  const result = await publicClient.mutate<{ ssoLogin: { firebaseToken: string } }>({
    mutation: SSO_LOGIN_MUTATION,
    variables: { input: { provider, payload } },
  })

  const firebaseToken = result.data?.ssoLogin?.firebaseToken
  if (!firebaseToken) {
    throw new Error('ssoLogin mutation returned no firebaseToken')
  }

  // Set auth_type BEFORE signInWithCustomToken so that onAuthStateChanged
  // (which Firebase fires synchronously during signIn) reads 'sso' and builds
  // the correct 'MagicLink <firebase_id_token>' header for GetCurrentUser.
  // Clean up on failure so we don't leave stale auth state.
  localStorage.setItem('auth_type', 'sso')
  try {
    await signInWithCustomToken(auth, firebaseToken)
  } catch (e) {
    localStorage.removeItem('isAuth')
    localStorage.removeItem('auth_type')
    throw e
  }
  await ensurePersistence()
  exitGuestMode()
}

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Exchanges an SSO provider payload for a Firebase custom token, then signs
 * the user in silently with signInWithCustomToken. Retries on failure with
 * a short backoff before giving up.
 *
 * @param provider - e.g. 'mondcasino'
 * @param payload  - provider-specific object, e.g. { sessionId: '...' }
 */
export const authBySso = async (
  provider: string,
  payload: unknown,
  onAttempt?: (attempt: number, maxAttempts: number) => void,
): Promise<void> => {
  const maxAttempts = SSO_RETRY_DELAYS_MS.length + 1
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    onAttempt?.(attempt, maxAttempts)
    try {
      await attemptSsoExchange(provider, payload)
      return
    } catch (e) {
      const isLastAttempt = attempt === maxAttempts
      if (isLastAttempt) {
        const reason = e instanceof Error ? e.message : String(e)
        throw new SsoLoginError(reason, attempt)
      }
      await wait(SSO_RETRY_DELAYS_MS[attempt - 1])
    }
  }
}

export const newUserOnboardingPath = (): string => (
  localStorage.getItem('onboardingFlow') === UserRoles.Tipster
    ? ROUTES.tipsterOnboardingPersonalInfo.full
    : ROUTES.loginPersonalInfo.full
)

export const onboardingRedirectUrl = (id, userType, currentOnboardingStage, userDataLoading = false) => {
  const isTipsterWorkflow = (userType || localStorage.getItem('onboardingFlow')) === UserRoles.Tipster

  // While the current-user fetch is still in flight, id is null regardless of
  // the real onboarding stage. Returning a guessed destination here (previously
  // defaulting to UpdatingPersonalInfo) caused a redirect-loop once the fetch
  // resolved and every guard re-evaluated against the real stage. Wait instead.
  if (userDataLoading && !id && localStorage.getItem('isAuth')) {
    return null
  }

  const userInterestsOnboardingURL = (
    ROUTES.loginInterests.full
      .replace(':onboardingStage', (currentOnboardingStage ?? '').toLowerCase().replaceAll('_', '-'))
  )

  const tipsterInterestsOnboardingURL = (
    ROUTES.tipsterOnboardingInterests.full
      .replace(':onboardingStage', (currentOnboardingStage ?? '').toLowerCase().replaceAll('_', '-'))
  )

  const onboardingRedirectPaths = {
    [ UserOnboardingStages.UpdatingPersonalInfo ]: isTipsterWorkflow
      ? ROUTES.tipsterOnboardingPersonalInfo.full
      : ROUTES.loginPersonalInfo.full,
    [ UserOnboardingStages.ChoosingFavoriteSports ]: isTipsterWorkflow
      ? tipsterInterestsOnboardingURL
      : userInterestsOnboardingURL,
    [ UserOnboardingStages.CreatingChannel ]: isTipsterWorkflow
      ? ROUTES.tipsterOnboardingChannel.full
      : ROUTES.feed.short,
  }

  return (
    localStorage.getItem('isAuth') ? onboardingRedirectPaths[
      id? currentOnboardingStage: UserOnboardingStages.UpdatingPersonalInfo
    ]: isTipsterWorkflow ? ROUTES.tipsterOnboarding.short : ROUTES.root
  )
}

const readToken = async () => {
  try {
    const authType = localStorage.getItem('auth_type')
    if (authType === 'magic_link' || authType === 'sso') {
      return getFirebaseToken()
    }
    const user = await getCurrentUser()
    if (authType === 'telegram') {
      const payload = JSON.stringify(user.data)
      const encoded = btoa(unescape(encodeURIComponent(payload)))
      return encoded
    } else if (authType === 'magic_link' || authType === 'sso') {
      // Both magic link and SSO use Firebase ID tokens
      return getFirebaseToken()
    } else {
      return user?.data?.signInUserSession?.idToken?.jwtToken || ''
    }
  } catch (e) {
    console.error('Error in getToken:', e)
    return ''
  }
}

// Every Apollo request asks for a token, and the app sends many at once on
// start-up. Parallel callers share one lookup, so an expired Cognito session
// is refreshed once instead of once per request.
let pendingToken: ReturnType<typeof readToken> | null = null

export const getToken = () => {
  if (!pendingToken) {
    pendingToken = readToken().finally(() => {
      pendingToken = null
    })
  }
  return pendingToken
}
